import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Grid, Card, Stack, Typography, InputAdornment } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { UserLevelType } from 'src/type/user';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import UpdatedText from 'src/components/ui-kit/updated-text';
import { levelLabel } from '../../../user/utils';

type Props = {
  levelFee: UserLevelType[];
  forceReload: () => void;
};

export default function SettingUserLevelFee({ levelFee, forceReload }: Props) {
  return (
    <Card sx={{ py: 5, px: 3 }}>
      <Stack spacing={3}>
        <Typography variant="h6">ค่าธรรมเนียมระดับผู้ใช้</Typography>

        {levelFee.map((lv) => (
          <UserLevelFeeForm levelFee={lv} key={lv.id} forceReload={forceReload} />
        ))}
      </Stack>
    </Card>
  );
}

function UserLevelFeeForm({
  levelFee,
  forceReload,
}: {
  levelFee: UserLevelType;
  forceReload: () => void;
}) {
  const Schema = Yup.object().shape({
    buyFee: Yup.number().required('จำเป็นต้องระบุค่าธรรมเนียมซื้อ'),
    sellFee: Yup.number().required('จำเป็นต้องระบุค่าธรรมเนียมซื้อ'),
  });

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues: {
      buyFee: levelFee?.buyFee || 0,
      sellFee: levelFee?.sellFee || 0,
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (values) => {
    try {
      await axios.patch(endpoints.userLevel.update(levelFee.id), { ...values });
      forceReload();
      enqueueSnackbar('บันทึกสำเร็จ');
    } catch (error) {
      toastError(error);
    }
  });

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={1.5} display="flex" alignItems="center">
          <Typography variant="caption">{levelLabel(levelFee.name)}</Typography>
        </Grid>
        <Grid item xs={12} sm={4.5}>
          <RHFTextField
            type="number"
            name="buyFee"
            label="ค่าธรรมเนียมซื้อ"
            placeholder="0"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography fontWeight="bold" color="success.main">
                    THB
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4.5}>
          <RHFTextField
            type="number"
            name="sellFee"
            label="ค่าธรรมเนียมขาย"
            placeholder="0"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography fontWeight="bold" color="success.main">
                    THB
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={1.5} display="flex" alignItems="center">
          <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
            บันทึก
          </LoadingButton>
        </Grid>
        <Grid item xs={12} sm={2} display="flex" alignItems="center" />

        <Grid item xs={12} sm={10}>
          <Typography variant="caption" color="text.secondary">
            <UpdatedText user={levelFee.approvedByUser} updatedAt={levelFee.updatedAt} />
          </Typography>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
